import {
    FloatingRootContext,
    Placement,
    ReferenceType
} from "@floating-ui/react";
import { isDefined } from "@omni/utils/helper.utils";
import { CSSProperties, createContext, ReactNode, RefObject, use } from "react";

type DropdownContextValue = {
    activeIndex: number | null;
    selectedIndex: number | null;
    placement: Placement;
    isLabel: boolean;
    isOpen: boolean;
    label: string | null;
    elementsRef: RefObject<Array<HTMLElement | null>>;
    labelsRef: RefObject<Array<string | null>>;
    floatingStyles: CSSProperties;
    floatingContext: FloatingRootContext<ReferenceType>;
    onSelect: (index: number | null) => void;
    getToggleProps: (
        userProps?: React.HTMLProps<Element>
    ) => Record<string, unknown>;
    getFloatingProps: (
        userProps?: React.HTMLProps<HTMLElement>
    ) => Record<string, unknown>;
    getItemProps: (
        userProps?: Omit<React.HTMLProps<HTMLElement>, "selected" | "active">
    ) => Record<string, unknown>;
    setToggleRef: (node: ReferenceType | null) => void;
    setFloatingRef: (node: HTMLElement | null) => void;
};

type DropdownContextProviderProps = {
    activeIndex: number | null;
    selectedIndex: number | null;
    placement: Placement;
    isLabel: boolean;
    isOpen: boolean;
    label: string | null;
    elementsRef: RefObject<Array<HTMLElement | null>>;
    labelsRef: RefObject<Array<string | null>>;
    floatingStyles: CSSProperties;
    floatingContext: FloatingRootContext<ReferenceType>;
    onSelect: (index: number | null) => void;
    getToggleProps: (
        userProps?: React.HTMLProps<Element>
    ) => Record<string, unknown>;
    getFloatingProps: (
        userProps?: React.HTMLProps<HTMLElement>
    ) => Record<string, unknown>;
    getItemProps: (
        userProps?: Omit<React.HTMLProps<HTMLElement>, "selected" | "active">
    ) => Record<string, unknown>;
    setToggleRef: (node: ReferenceType | null) => void;
    setFloatingRef: (node: HTMLElement | null) => void;
    children: ReactNode;
};

export const DropdownContext = createContext<DropdownContextValue | undefined>(
    undefined
);
DropdownContext.displayName = "DropdownContext";

export const DropdownContextProvider = ({
    activeIndex,
    selectedIndex,
    placement,
    isLabel,
    isOpen,
    label,
    elementsRef,
    labelsRef,
    floatingStyles,
    floatingContext,
    onSelect,
    getToggleProps,
    getFloatingProps,
    getItemProps,
    setToggleRef,
    setFloatingRef,
    children
}: DropdownContextProviderProps) => {
    return (
        <DropdownContext
            value={{
                activeIndex: activeIndex,
                selectedIndex: selectedIndex,
                placement: placement,
                isLabel: isLabel,
                isOpen: isOpen,
                label: label,
                elementsRef: elementsRef,
                labelsRef: labelsRef,
                floatingStyles: floatingStyles,
                floatingContext: floatingContext,
                onSelect: onSelect,
                getToggleProps: getToggleProps,
                getFloatingProps: getFloatingProps,
                getItemProps: getItemProps,
                setToggleRef: setToggleRef,
                setFloatingRef: setFloatingRef
            }}
        >
            {children}
        </DropdownContext>
    );
};

export const useDropdownContext = () => {
    const context = use<DropdownContextValue | undefined>(DropdownContext);

    if (!isDefined(context)) {
        throw new Error(`${DropdownContext.displayName} is undefined.`);
    }

    return context;
};
